/**
 * Real-time User Activity Status Service
 * Handles user activity tracking, status updates, and real-time synchronization
 */

import apiService from './apiService.js';
import sessionService from './sessionService.js';


class UserActivityService {
  constructor() {
    this.activityCache = new Map();
    this.listeners = new Set();
    this.pollingInterval = null;
    this.websocket = null;
    this.isPolling = false;
    this.pollingIntervalMs = 30000; // 30 seconds
    this.timestampUpdateInterval = null;
    this.lastUpdateTime = null;
  }

  /**
   * Initialize the activity service
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('🔄 User Activity Service already initialized');
      return;
    }

    console.log('🔄 Initializing User Activity Service...');
    
    try {
      // Check if user is authenticated before initializing
      if (!sessionService.isAuthenticated()) {
        console.log('ℹ️ User not authenticated, skipping user activity service initialization');
        return;
      }

      // Try to establish WebSocket connection first
      await this.initializeWebSocket();
      
      // Start periodic polling as fallback
      this.startPolling();
      
      // Start timestamp updates
      this.startTimestampUpdates();
      
      this.isInitialized = true;
      console.log('✅ User Activity Service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize User Activity Service:', error);
      // Fallback to polling only
      this.startPolling();
      this.startTimestampUpdates();
    }
  }

  /**
   * Initialize WebSocket connection for real-time updates
   */
  async initializeWebSocket() {
    try {
      // Check if WebSocket service is available
      if (window.websocketService) {
        console.log('🔌 Setting up WebSocket listeners for user activity...');
        
        // Listen for user activity updates
        window.websocketService.on('user_activity_update', this.handleActivityUpdate.bind(this));
        window.websocketService.on('user_status_change', this.handleStatusChange.bind(this));
        
        console.log('✅ WebSocket listeners set up for user activity');
      } else {
        console.log('⚠️ WebSocket service not available, using polling only');
      }
    } catch (error) {
      console.warn('⚠️ Failed to set up WebSocket for user activity:', error);
    }
  }

  /**
   * Start periodic polling for activity updates
   */
  startPolling() {
    if (this.isPolling) {
      console.log('🔄 Activity polling already running');
      return;
    }

    console.log(`🔄 Starting activity polling every ${this.pollingIntervalMs / 1000} seconds`);
    this.isPolling = true;

    // Initial fetch
    this.fetchAllUserActivity();

    // Set up interval
    this.pollingInterval = setInterval(() => {
      this.fetchAllUserActivity();
    }, this.pollingIntervalMs);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollingInterval) {
      console.log('⏹️ Stopping activity polling');
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.isPolling = false;
    }
  }

  /**
   * Start timestamp updates every minute
   */
  startTimestampUpdates() {
    console.log('🕐 Starting timestamp updates every minute');
    
    this.timestampUpdateInterval = setInterval(() => {
      this.notifyTimestampUpdate();
    }, 60000); // 1 minute
  }

  /**
   * Stop timestamp updates
   */
  stopTimestampUpdates() {
    if (this.timestampUpdateInterval) {
      console.log('⏹️ Stopping timestamp updates');
      clearInterval(this.timestampUpdateInterval);
      this.timestampUpdateInterval = null;
    }
  }

  /**
   * Fetch activity status for all users in the organization
   */
  async fetchAllUserActivity() {
    try {
      console.log('🔍 Fetching user activity status...');
      
      // Check if user is authenticated
      if (!sessionService.isAuthenticated()) {
        console.log('ℹ️ User not authenticated, skipping activity fetch');
        return;
      }

      // Get current organization
      const currentOrgId = sessionService.getOrganizationId();
      if (!currentOrgId) {
        console.warn('⚠️ No current organization found');
        return;
      }

      // Fetch activity data from API
      const response = await apiService.users.getActivityStatus(currentOrgId);
      
      if (response && response.data) {
        this.updateActivityCache(response.data);
        this.notifyListeners('activity_updated', response.data);
        this.lastUpdateTime = Date.now();
        console.log(`✅ Updated activity for ${response.data.length} users`);
      }
    } catch (error) {
      console.error('❌ Failed to fetch user activity:', error);
      this.notifyListeners('activity_error', error);
    }
  }

  /**
   * Get current organization from session
   */
  getCurrentOrganization() {
    try {
      const orgId = sessionService.getOrganizationId();
      if (orgId) return { id: orgId };
      
      // Fallback to localStorage (legacy)
      const orgData = localStorage.getItem('currentOrganization');
      return orgData ? JSON.parse(orgData) : null;
    } catch (error) {
      console.warn('Failed to get current organization:', error);
      return null;
    }
  }

  /**
   * Update activity cache with new data
   */
  updateActivityCache(activityData) {
    if (!Array.isArray(activityData)) {
      console.warn('Invalid activity data format:', activityData);
      return;
    }

    activityData.forEach(userActivity => {
      if (userActivity.user_id) {
        this.activityCache.set(userActivity.user_id, {
          ...userActivity,
          cached_at: Date.now()
        });
      }
    });
  }

  /**
   * Handle WebSocket activity update
   */
  handleActivityUpdate(data) {
    console.log('🔌 Received activity update via WebSocket:', data);
    
    if (data.user_id) {
      this.activityCache.set(data.user_id, {
        ...data,
        cached_at: Date.now()
      });
      
      this.notifyListeners('user_activity_updated', data);
    }
  }

  /**
   * Handle WebSocket status change
   */
  handleStatusChange(data) {
    console.log('🔌 Received status change via WebSocket:', data);
    
    if (data.user_id) {
      const existing = this.activityCache.get(data.user_id) || {};
      this.activityCache.set(data.user_id, {
        ...existing,
        ...data,
        cached_at: Date.now()
      });
      
      this.notifyListeners('user_status_changed', data);
    }
  }

  /**
   * Get activity status for a specific user
   */
  getUserActivity(userId) {
    const activity = this.activityCache.get(userId);
    
    if (!activity) {
      return {
        user_id: userId,
        is_active: false,
        last_activity: null,
        status: 'offline'
      };
    }

    // Check if cached data is still fresh (within 2 minutes)
    const cacheAge = Date.now() - (activity.cached_at || 0);
    const isStale = cacheAge > 120000; // 2 minutes

    return {
      ...activity,
      is_stale: isStale
    };
  }

  /**
   * Get activity status for all cached users
   */
  getAllUserActivity() {
    const allActivity = [];
    
    for (const [userId, activity] of this.activityCache.entries()) {
      allActivity.push(this.getUserActivity(userId));
    }
    
    return allActivity;
  }

  /**
   * Format timestamp to human-readable relative time
   */
  formatRelativeTime(timestamp) {
    if (!timestamp) return 'Never';
    
    const now = Date.now();
    const activityTime = new Date(timestamp).getTime();
    const diffMs = now - activityTime;
    
    // If less than 1 minute, show as active
    if (diffMs < 60000) {
      return 'Active now';
    }
    
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks}w ago`;
    }
  }

  /**
   * Check if user is currently active
   */
  isUserActive(userId) {
    const activity = this.getUserActivity(userId);
    
    if (!activity.last_activity) {
      return false;
    }
    
    // Consider user active if last activity was within 5 minutes
    const now = Date.now();
    const lastActivity = new Date(activity.last_activity).getTime();
    const diffMs = now - lastActivity;
    
    return diffMs < 300000; // 5 minutes
  }

  /**
   * Add listener for activity updates
   */
  addListener(callback) {
    console.log('👂 Adding activity listener');
    this.listeners.add(callback);
    
    return () => {
      console.log('🗑️ Removing activity listener');
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in activity listener:', error);
      }
    });
  }

  /**
   * Notify listeners about timestamp updates
   */
  notifyTimestampUpdate() {
    this.notifyListeners('timestamps_updated', {
      timestamp: Date.now()
    });
  }

  /**
   * Update user's own activity
   */
  async updateOwnActivity() {
    try {
      // Check if user is authenticated
      if (!sessionService.isAuthenticated()) {
        return;
      }
      await apiService.users.updateActivity();
      console.log('✅ Updated own activity status');
    } catch (error) {
      console.warn('⚠️ Failed to update own activity:', error);
    }
  }

  /**
   * Cleanup service
   */
  cleanup() {
    console.log('🧹 Cleaning up User Activity Service...');
    
    this.stopPolling();
    this.stopTimestampUpdates();
    
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    
    this.activityCache.clear();
    this.listeners.clear();
    
    console.log('✅ User Activity Service cleaned up');
  }
}

// Create singleton instance
const userActivityService = new UserActivityService();

export default userActivityService;
